import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackToTaxFormsBanner from '../components/BackToTaxFormsBanner';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { Building, Edit, Trash2, Plus, FileText, BarChart3, Home, DollarSign, Calendar, User, Phone, Mail, TrendingUp, Send, Eye, ToggleLeft, ToggleRight, MapPin } from 'lucide-react';
import ContractManager from '../components/ContractManager';
import ValidatedInput from '../components/ValidatedInput';
import ValidatedSelect from '../components/ValidatedSelect';
import ValidatedTextarea from '../components/ValidatedTextarea';
import { validationRules } from '../utils/validation';
import Disclaimer from '../components/Disclaimer';
import '../components/ValidatedInput.css';

const PropertyManager = () => {
  const [properties, setProperties] = useState([]);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showEditProperty, setShowEditProperty] = useState(false);
  const [showAddContract, setShowAddContract] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('properties');
  const [fetchingData, setFetchingData] = useState(false);
  // Form hook for add property form
  const { register: registerProperty, handleSubmit: handleSubmitProperty, formState: { errors: propertyErrors }, reset: resetProperty, watch: watchProperty } = useForm({
    defaultValues: {
      name: '',
      type: 'rental',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      property_value: '',
      has_mortgage: 'no',
      is_usa: 'yes',
      loan_amount: '',
      loan_rate: '',
      loan_term: '',
      monthly_rent: '',
      monthly_expenses: '',
      status: 'active',
      notes: '',
      purchase_date: '',
      purchase_price: '',
      rental_start_date: '',
      rental_type: 'whole_house',
      total_bedrooms: '',
      total_bathrooms: '',
      square_footage: '',
      year_built: '',
      property_description: '',
      amenities: ''
    }
  });

  const watchedPropertyType = watchProperty('type');
  const watchedHasMortgage = watchProperty('has_mortgage');

  const [newProperty, setNewProperty] = useState({
    name: '',
    type: 'rental',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    property_value: '',
    has_mortgage: 'no',
    is_usa: 'yes',
    loan_amount: '',
    loan_rate: '',
    loan_term: '',
    monthly_rent: '',
    monthly_expenses: '',
    status: 'active',
    notes: '',
    purchase_date: '',
    purchase_price: '',
    rental_start_date: '',
    rental_type: 'whole_house',
    total_bedrooms: '',
    total_bathrooms: '',
    square_footage: '',
    year_built: '',
    property_description: '',
    amenities: ''
  });
  const [newContract, setNewContract] = useState({
    tenant_name: '',
    tenant_email: '',
    tenant_phone: '',
    monthly_rent: '',
    security_deposit: '',
    lease_start: '',
    lease_end: '',
    status: 'draft',
    contract_type: 'whole_house',
    room_id: null
  });

  // New state for enhanced property management
  const [rooms, setRooms] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [newRoom, setNewRoom] = useState({
    room_name: '',
    room_type: 'bedroom',
    room_description: '',
    monthly_rent: ''
  });
  const [newTenant, setNewTenant] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    drivers_license: '',
    drivers_license_state: '',
    drivers_license_expiry: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    employment_company: '',
    employment_position: '',
    employment_phone: '',
    monthly_income: '',
    credit_score: '',
    tenant_references: '',
    notes: '',
    room_id: null
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      console.log('🔍 Fetching properties...');
      const token = localStorage.getItem('accessToken');
      console.log('🔍 Token:', token ? 'Present' : 'Missing');
      
      const response = await axios.get('/api/properties', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('🔍 Properties response:', response.data);
      console.log('🔍 Properties array:', response.data.data);
      
      setProperties(response.data.data || []);
      console.log('🔍 Properties state updated:', response.data.data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyData = async (propertyId) => {
    try {
      setFetchingData(true);
      const response = await axios.post(`/api/property-data/${propertyId}/fetch`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      
      if (response.data.success) {
        toast.success(`Property data updated: Value $${response.data.data.new_value?.toLocaleString()}, Tax $${response.data.data.new_annual_tax?.toLocaleString()}`);
        // Refresh properties to show updated data
        await fetchProperties();
      }
    } catch (error) {
      console.error('Error fetching property data:', error);
      toast.error('Failed to fetch property data');
    } finally {
      setFetchingData(false);
    }
  };

  const handleAddProperty = async (data) => {
    try {
      // Ensure state is uppercase and exactly 2 characters
      const propertyData = {
        ...data,
        state: (data.state || '').toUpperCase().substring(0, 2),
        has_mortgage: data.has_mortgage || 'no',
        is_usa: data.is_usa || 'yes',
        type: data.type || 'rental'
      };
      
      // Convert empty strings to null for optional numeric fields
      const numericFields = ['property_value', 'loan_amount', 'loan_rate', 'loan_term', 'monthly_rent', 'monthly_expenses', 'purchase_price', 'total_bedrooms', 'total_bathrooms', 'square_footage', 'year_built'];
      numericFields.forEach(field => {
        if (propertyData[field] === '' || propertyData[field] === null || propertyData[field] === undefined) {
          propertyData[field] = null;
        } else if (typeof propertyData[field] === 'string') {
          const num = parseFloat(propertyData[field]);
          propertyData[field] = isNaN(num) ? null : num;
        }
      });
      
      console.log('🔍 Sending property data:', propertyData);
      
      const token = localStorage.getItem('accessToken');
      const response = await axios.post('/api/properties', propertyData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      console.log('Response data:', response.data);

      if (response.data.success) {
        toast.success('Property added successfully!');
        setShowAddProperty(false);
        resetProperty();
        setNewProperty({
          name: '',
          type: 'rental',
          address: '',
          city: '',
          state: '',
          zip_code: '',
          property_value: '',
          has_mortgage: 'no',
          loan_amount: '',
          loan_rate: '',
          loan_term: '',
          monthly_rent: '',
          monthly_expenses: '',
          status: 'active',
          notes: '',
          purchase_date: '',
          purchase_price: '',
          rental_start_date: ''
        });
        fetchProperties();
      } else {
        console.error('API returned error:', response.data.error);
        if (response.data.details && response.data.details.length > 0) {
          const errorMessages = response.data.details.map(detail => detail.message).join(', ');
          toast.error(`Validation failed: ${errorMessages}`);
        } else {
          toast.error(response.data.error || 'Failed to add property');
        }
      }
    } catch (error) {
      console.error('Error adding property:', error);
      if (error.response?.data?.details) {
        const errorMessages = error.response.data.details.map(detail => detail.message).join(', ');
        toast.error(`Validation failed: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.error || 'Failed to add property');
      }
    }
  };

  const handleUpdateProperty = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // Remove fields that shouldn't be sent to the server (keep purchase_price and rental_start_date for depreciation)
      const { id, user_id, created_at, updated_at, data_sources, source_urls, estimation_method, confidence_level, ...updateData } = selectedProperty;
      
      console.log('🔍 Updating property with data:', updateData);
      
      console.log('🔍 Sending property update:', updateData);
      
      const response = await axios.put(`/api/properties/${id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success('Property updated successfully!');
        setShowEditProperty(false);
        setSelectedProperty(null);
        fetchProperties();
      } else {
        toast.error(response.data.error || 'Failed to update property');
      }
    } catch (error) {
      console.error('❌ Error updating property:', error);
      console.error('❌ Error response:', error.response?.data);
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        console.error('❌ Validation errors:', error.response.data.details);
        const errorMessages = error.response.data.details.map(detail => detail.message).join(', ');
        toast.error(`Validation failed: ${errorMessages}`);
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to update property');
      }
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.delete(`/api/properties/${propertyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          toast.success('Property deleted successfully!');
          fetchProperties();
        } else {
          toast.error(response.data.error || 'Failed to delete property');
        }
      } catch (error) {
        console.error('Error deleting property:', error);
        toast.error('Failed to delete property');
      }
    }
  };

  const handleUpdatePropertyValue = async (propertyId) => {
    try {
      const token = localStorage.getItem('accessToken');
      toast.loading('Updating property value from Zillow...');
      
      const response = await axios.post(`/api/properties/${propertyId}/update-value`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        toast.dismiss();
        toast.success(`Property value updated! New value: $${response.data.data.new_value.toLocaleString()}`);
        fetchProperties(); // Refresh the properties list
      } else {
        toast.dismiss();
        toast.error(response.data.error || 'Failed to update property value');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error updating property value:', error);
      toast.error('Failed to update property value from Zillow');
    }
  };

  const handleTogglePropertyType = async (property) => {
    const newType = property.type === 'rental' ? 'primary' : 'rental';
    const confirmMessage = `Switch ${property.name} from ${property.type} to ${newType}?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      
      // Remove fields that shouldn't be sent to the server (keep purchase_price and rental_start_date for depreciation)
      const { id, user_id, created_at, updated_at, data_sources, source_urls, estimation_method, confidence_level, ...updateData } = property;
      
      console.log('🔍 Toggling property with updateData:', updateData);
      
      const response = await axios.put(`/api/properties/${property.id}`, {
        ...updateData,
        type: newType
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success(`Property switched to ${newType}!`);
        fetchProperties(); // Refresh the properties list
      } else {
        toast.error(response.data.error || 'Failed to update property type');
      }
    } catch (error) {
      console.error('❌ Error toggling property type:', error);
      console.error('❌ Error response:', error.response?.data);
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        console.error('❌ Validation errors:', error.response.data.details);
        const errorMessages = error.response.data.details.map(detail => detail.message).join(', ');
        toast.error(`Validation failed: ${errorMessages}`);
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to update property type');
      }
    }
  };

  const handleUpdateAllPropertyValues = async () => {
    if (properties.length === 0) {
      toast.error('No properties to update');
      return;
    }

    if (!window.confirm(`Update property values for all ${properties.length} properties? This may take a few minutes.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const usaProperties = properties.filter(p => p.is_usa === 'yes');
      toast.loading(`Updating ${usaProperties.length} USA properties with values & tax info from county assessors...`);
      
      const response = await axios.post('/api/properties/update-all-values', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        toast.dismiss();
        const { updated_count, total_properties, usa_properties } = response.data.data;
        toast.success(`Updated ${updated_count} out of ${usa_properties} USA properties with values & tax info!`);
        fetchProperties(); // Refresh the properties list
      } else {
        toast.dismiss();
        toast.error(response.data.error || 'Failed to update property values');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error updating all property values:', error);
      toast.error('Failed to update property values & tax info from county assessors');
    }
  };

  const handleAddContract = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`/api/properties/${selectedProperty.id}/renter-contracts`, newContract, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success('Contract added successfully!');
        setShowAddContract(false);
        setNewContract({
          tenant_name: '',
          tenant_email: '',
          tenant_phone: '',
          monthly_rent: '',
          security_deposit: '',
          lease_start: '',
          lease_end: '',
          status: 'draft',
          contract_type: 'whole_house',
          room_id: null
        });
      } else {
        toast.error(response.data.error || 'Failed to add contract');
      }
    } catch (error) {
      console.error('Error adding contract:', error);
      toast.error('Failed to add contract');
    }
  };

  // New functions for enhanced property management
  const fetchPropertyRooms = async (propertyId) => {
    try {
      const response = await axios.get(`/api/properties/${propertyId}/rooms`);
      setRooms(response.data.data);
    } catch (error) {
      console.error('Error fetching property rooms:', error);
      toast.error('Failed to fetch property rooms');
    }
  };

  const fetchPropertyTenants = async (propertyId) => {
    try {
      const response = await axios.get(`/api/properties/${propertyId}/tenants`);
      setTenants(response.data.data);
    } catch (error) {
      console.error('Error fetching property tenants:', error);
      toast.error('Failed to fetch property tenants');
    }
  };

  const fetchPropertyContracts = async (propertyId) => {
    try {
      const response = await axios.get(`/api/properties/${propertyId}/contracts`);
      setContracts(response.data.data);
    } catch (error) {
      console.error('Error fetching property contracts:', error);
      toast.error('Failed to fetch property contracts');
    }
  };

  const handleAddRoom = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`/api/properties/${selectedProperty.id}/rooms`, newRoom);

      if (response.data.success) {
        toast.success('Room added successfully!');
        setShowAddRoom(false);
        setNewRoom({
          room_name: '',
          room_type: 'bedroom',
          room_description: '',
          monthly_rent: ''
        });
        fetchPropertyRooms(selectedProperty.id);
      } else {
        toast.error(response.data.error || 'Failed to add room');
      }
    } catch (error) {
      console.error('Error adding room:', error);
      toast.error('Failed to add room');
    }
  };

  const handleAddTenant = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`/api/properties/${selectedProperty.id}/tenants`, newTenant);

      if (response.data.success) {
        toast.success('Tenant added successfully!');
        setShowAddTenant(false);
        setNewTenant({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          drivers_license: '',
          drivers_license_state: '',
          drivers_license_expiry: '',
          emergency_contact_name: '',
          emergency_contact_phone: '',
          emergency_contact_relationship: '',
          employment_company: '',
          employment_position: '',
          employment_phone: '',
          monthly_income: '',
          credit_score: '',
          tenant_references: '',
          notes: '',
          room_id: null
        });
        fetchPropertyTenants(selectedProperty.id);
      } else {
        toast.error(response.data.error || 'Failed to add tenant');
      }
    } catch (error) {
      console.error('Error adding tenant:', error);
      toast.error('Failed to add tenant');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <BackToTaxFormsBanner />
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Property Manager</h1>
          <Link
            to="/property-tax-questionnaire"
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <FileText className="h-4 w-4" />
            Rental Tax Questionnaire
          </Link>
        </div>
        <p className="text-gray-600">Manage your real estate portfolio and renter contracts</p>
      </div>
      <Disclaimer variant="general" />

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('properties')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'properties'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building className="inline-block w-4 h-4 mr-2" />
            Properties ({properties.length})
          </button>
          <button
            onClick={() => setActiveTab('rooms')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'rooms'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Home className="inline-block w-4 h-4 mr-2" />
            Rooms
          </button>
          <button
            onClick={() => setActiveTab('tenants')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tenants'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <User className="inline-block w-4 h-4 mr-2" />
            Tenants
          </button>
          <button
            onClick={() => setActiveTab('contracts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'contracts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="inline-block w-4 h-4 mr-2" />
            Contracts
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BarChart3 className="inline-block w-4 h-4 mr-2" />
            Portfolio Analytics
          </button>
        </nav>
      </div>

      {/* Properties Tab */}
      {activeTab === 'properties' && (
        <div>
          {/* Property Action Buttons */}
          <div className="mb-6 flex flex-wrap gap-3">
            <button
              onClick={() => setShowAddProperty(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </button>
            <Link
              to="/mortgage-calculator"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Mortgage Calculator — fill in loans & escrow
            </Link>
            <button
              onClick={handleUpdateAllPropertyValues}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Update All Values & Tax Info from County Assessors
            </button>
          </div>

          {/* Properties Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <div key={property.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{property.name}</h3>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-gray-500 capitalize">{property.type}</p>
                      <button
                        onClick={() => handleTogglePropertyType(property)}
                        className={`text-xs font-medium px-2 py-1 rounded-full transition-all ${
                          property.type === 'rental' 
                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                        title={`Switch to ${property.type === 'rental' ? 'Primary' : 'Rental'}`}
                      >
                        {property.type === 'rental' ? '🏠 Rental' : '🏡 Primary'}
                      </button>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleUpdatePropertyValue(property.id)}
                      className="text-green-600 hover:text-green-800"
                      title="Update Value & Tax Info from County Assessor"
                    >
                      <TrendingUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => fetchPropertyData(property.id)}
                      disabled={fetchingData}
                      className="text-purple-600 hover:text-purple-800 disabled:opacity-50"
                      title="Fetch Property Data (Value & Tax Estimation)"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedProperty(property);
                        setShowEditProperty(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProperty(property.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-600">
                    <Home className="inline-block w-3 h-3 mr-1" />
                    {property.address}, {property.city}, {property.state} {property.zip_code}
                  </p>
                  {property.property_value && (
                    <div className="text-sm text-gray-600">
                      <p>
                        <DollarSign className="inline-block w-3 h-3 mr-1" />
                        Value: ${property.property_value.toLocaleString()}
                      </p>
                      {property.data_sources && (
                        <p className="text-xs text-blue-600 mt-1">
                          📊 Source: {(Array.isArray(property.data_sources) 
                            ? property.data_sources 
                            : (() => { try { return JSON.parse(property.data_sources || '[]'); } catch { return []; } })()).join(', ')}
                        </p>
                      )}
                      {property.source_urls && (
                        <div className="text-xs text-blue-600 mt-1">
                          🔗 Links: {(Array.isArray(property.source_urls) 
                            ? property.source_urls 
                            : (() => { try { return JSON.parse(property.source_urls || '[]'); } catch { return []; } })()).map((url, index) => (
                            <a 
                              key={index}
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="underline hover:text-blue-800 mr-2"
                            >
                              {url.split('//')[1]?.split('/')[0] || url}
                            </a>
                          ))}
                        </div>
                      )}
                      {property.estimation_method && (
                        <p className="text-xs text-gray-500 mt-1">
                          🔧 Method: {property.estimation_method}
                        </p>
                      )}
                      {property.confidence_level && (
                        <p className="text-xs text-gray-500 mt-1">
                          🎯 Confidence: {property.confidence_level}
                        </p>
                      )}
                    </div>
                  )}
                  {property.annual_tax && (
                    <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
                      <p className="font-medium text-blue-800">🏛️ Property Taxes:</p>
                      <p>Annual Tax: ${property.annual_tax.toLocaleString()}</p>
                      {property.tax_rate && (
                        <p>Tax Rate: {property.tax_rate}%</p>
                      )}
                      {property.assessment_date && (
                        <p>Assessed: {new Date(property.assessment_date).toLocaleDateString()}</p>
                      )}
                      {property.payment_status && (
                        <p className={`font-medium ${
                          property.payment_status === 'current' || property.payment_status === 'paid' 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          Status: {property.payment_status.charAt(0).toUpperCase() + property.payment_status.slice(1)}
                        </p>
                      )}
                    </div>
                  )}
                  {property.monthly_rent && (
                    <p className="text-sm text-gray-600">
                      <DollarSign className="inline-block w-3 h-3 mr-1" />
                      Rent: ${property.monthly_rent}/month
                    </p>
                  )}
                  
                  {/* Rental-specific details */}
                  {property.type === 'rental' && (
                    <div className="text-sm text-gray-600 bg-purple-50 p-2 rounded">
                      <p className="font-medium text-purple-800">🏠 Property Details:</p>
                      {property.rental_type && (
                        <p>Type: {property.rental_type === 'whole_house' ? 'Whole House' : 'By Room'}</p>
                      )}
                      {property.total_bedrooms > 0 && (
                        <p>Bedrooms: {property.total_bedrooms}</p>
                      )}
                      {property.total_bathrooms > 0 && (
                        <p>Bathrooms: {property.total_bathrooms}</p>
                      )}
                      {property.square_footage > 0 && (
                        <p>Square Footage: {property.square_footage.toLocaleString()}</p>
                      )}
                      {property.year_built > 0 && (
                        <p>Year Built: {property.year_built}</p>
                      )}
                      {property.property_description && (
                        <p className="mt-1 text-xs text-gray-500">{property.property_description}</p>
                      )}
                      {property.amenities && (
                        <p className="mt-1 text-xs text-gray-500">Amenities: {property.amenities}</p>
                      )}
                    </div>
                  )}
                  {property.has_mortgage === 'yes' && (
                    <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
                      <p className="font-medium text-blue-800">🏠 Mortgage Details:</p>
                      <p>Loan: ${property.loan_amount?.toLocaleString() || 'N/A'}</p>
                      <p>Rate: {property.loan_rate || 'N/A'}%</p>
                      <p>Term: {property.loan_term || 'N/A'} years</p>
                      <Link
                        to="/mortgage-calculator"
                        className="inline-block mt-2 text-xs font-semibold text-indigo-700 hover:underline"
                      >
                        Open Mortgage Calculator →
                      </Link>
                    </div>
                  )}
                  {property.has_mortgage !== 'yes' && (property.type === 'rental' || property.type === 'investment') && (
                    <Link
                      to="/mortgage-calculator"
                      className="block text-sm text-indigo-700 bg-indigo-50 hover:bg-indigo-100 p-2 rounded font-medium"
                    >
                      + Set up mortgage / escrow for this rental →
                    </Link>
                  )}
                  
                  {/* USA Property Indicator */}
                  <div className={`text-sm p-2 rounded ${
                    property.is_usa === 'yes' 
                      ? 'bg-green-50 text-green-800' 
                      : 'bg-orange-50 text-orange-800'
                  }`}>
                    <span className="font-medium">
                      {property.is_usa === 'yes' ? '🇺🇸 USA Property' : '🌍 International Property'}
                    </span>
                    {property.is_usa === 'yes' && (
                      <p className="text-xs mt-1">✓ Eligible for county assessor value & tax updates</p>
                    )}
                    {property.is_usa === 'no' && (
                      <p className="text-xs mt-1">⚠️ Manual value updates only</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    property.status === 'active' ? 'bg-green-100 text-green-800' :
                    property.status === 'inactive' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {property.status}
                  </span>
                  <button
                    onClick={() => {
                      setSelectedProperty(property);
                      setShowAddContract(true);
                      // Fetch related data for rental properties
                      if (property.type === 'rental') {
                        fetchPropertyRooms(property.id);
                        fetchPropertyTenants(property.id);
                        fetchPropertyContracts(property.id);
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    <Plus className="inline-block w-3 h-3 mr-1" />
                    Add Contract
                  </button>
                </div>
              </div>
            ))}
          </div>

          {properties.length === 0 && (
            <div className="text-center py-12">
              <Building className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No properties</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first property.</p>
            </div>
          )}
        </div>
      )}

      {/* Rooms Tab */}
      {activeTab === 'rooms' && (
        <div>
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Property Rooms</h2>
            <button
              onClick={() => setShowAddRoom(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Room
            </button>
          </div>
          
          {selectedProperty ? (
            <div>
              <p className="text-gray-600 mb-4">Managing rooms for: <strong>{selectedProperty.name}</strong></p>
              {rooms.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rooms.map(room => (
                    <div key={room.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900">{room.room_name}</h3>
                      <p className="text-sm text-gray-600 capitalize">{room.room_type}</p>
                      <p className="text-sm text-gray-500 mt-1">{room.room_description}</p>
                      <p className="text-lg font-semibold text-green-600 mt-2">${room.monthly_rent}/month</p>
                      {room.is_rented ? (
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Rented
                          </span>
                          {room.first_name && (
                            <p className="text-sm text-gray-600 mt-1">
                              Tenant: {room.first_name} {room.last_name}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-2">
                          Available
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Home className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No rooms added</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by adding rooms to this property.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Home className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Select a property</h3>
              <p className="mt-1 text-sm text-gray-500">Choose a property from the Properties tab to manage its rooms.</p>
            </div>
          )}
        </div>
      )}

      {/* Tenants Tab */}
      {activeTab === 'tenants' && (
        <div>
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Property Tenants</h2>
            <button
              onClick={() => setShowAddTenant(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tenant
            </button>
          </div>
          
          {selectedProperty ? (
            <div>
              <p className="text-gray-600 mb-4">Managing tenants for: <strong>{selectedProperty.name}</strong></p>
              {tenants.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tenants.map(tenant => (
                    <div key={tenant.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900">{tenant.first_name} {tenant.last_name}</h3>
                      <p className="text-sm text-gray-600">{tenant.email}</p>
                      <p className="text-sm text-gray-600">{tenant.phone}</p>
                      {tenant.room_name && (
                        <p className="text-sm text-gray-500 mt-1">Room: {tenant.room_name}</p>
                      )}
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                          tenant.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {tenant.status}
                        </span>
                      </div>
                      {tenant.drivers_license && (
                        <p className="text-xs text-gray-500 mt-1">DL: {tenant.drivers_license}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <User className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No tenants added</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by adding tenants to this property.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <User className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Select a property</h3>
              <p className="mt-1 text-sm text-gray-500">Choose a property from the Properties tab to manage its tenants.</p>
            </div>
          )}
        </div>
      )}

      {/* Renter Contracts Tab */}
      {activeTab === 'contracts' && (
        <div>
          <ContractManager 
            selectedProperty={selectedProperty}
            properties={properties}
            onContractCreated={() => {
              // Refresh contracts if needed
              if (selectedProperty) {
                fetchPropertyContracts(selectedProperty.id);
              }
            }}
          />
        </div>
      )}

      {/* Portfolio Analytics Tab */}
      {activeTab === 'analytics' && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Portfolio Analytics</h2>
          <p className="text-gray-600">Track your real estate portfolio performance and metrics.</p>
        </div>
      )}

      {/* Add Property Modal */}
      {showAddProperty && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Property</h3>
              <form onSubmit={handleSubmitProperty(handleAddProperty)}>
                <div className="space-y-4">
                  <ValidatedInput
                    name="name"
                    label="Property Name"
                    type="text"
                    register={registerProperty}
                    errors={propertyErrors}
                    placeholder="Enter property name"
                    icon={Building}
                    required
                    {...registerProperty('name', {
                      required: 'Property name is required',
                      minLength: {
                        value: 2,
                        message: 'Property name must be at least 2 characters'
                      }
                    })}
                  />

                  <ValidatedSelect
                    name="type"
                    label="Property Type"
                    register={registerProperty}
                    errors={propertyErrors}
                    placeholder="Select property type"
                    icon={Home}
                    required
                    options={[
                      { value: 'rental', label: 'Rental' },
                      { value: 'primary', label: 'Primary Residence' },
                      { value: 'investment', label: 'Investment' },
                      { value: 'future', label: 'Future Development' }
                    ]}
                    {...registerProperty('type', {
                      required: 'Property type is required'
                    })}
                  />

                  {/* Rental Type Selection - Only show for rental properties */}
                  {watchedPropertyType === 'rental' && (
                    <ValidatedSelect
                      name="rental_type"
                      label="Rental Type"
                      register={registerProperty}
                      errors={propertyErrors}
                      placeholder="Select rental type"
                      options={[
                        { value: 'whole_house', label: 'Whole House Rental' },
                        { value: 'by_room', label: 'Room-by-Room Rental' }
                      ]}
                      {...registerProperty('rental_type')}
                    />
                  )}

                  {/* Property Details - Show for all properties */}
                  <div className="grid grid-cols-2 gap-4">
                    <ValidatedInput
                      name="total_bedrooms"
                      label="Total Bedrooms"
                      type="number"
                      register={registerProperty}
                      errors={propertyErrors}
                      placeholder="0"
                      min="0"
                      {...registerProperty('total_bedrooms', {
                        ...validationRules.positiveNumber,
                        validate: (value) => {
                          if (!value) return true;
                          const num = parseInt(value);
                          if (isNaN(num) || num < 0 || num > 50) {
                            return 'Bedrooms must be between 0 and 50';
                          }
                          return true;
                        }
                      })}
                    />
                    <ValidatedInput
                      name="total_bathrooms"
                      label="Total Bathrooms"
                      type="number"
                      register={registerProperty}
                      errors={propertyErrors}
                      placeholder="0"
                      min="0"
                      step="0.5"
                      {...registerProperty('total_bathrooms', {
                        ...validationRules.positiveNumber,
                        validate: (value) => {
                          if (!value) return true;
                          const num = parseFloat(value);
                          if (isNaN(num) || num < 0 || num > 20) {
                            return 'Bathrooms must be between 0 and 20';
                          }
                          return true;
                        }
                      })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <ValidatedInput
                      name="square_footage"
                      label="Square Footage"
                      type="number"
                      register={registerProperty}
                      errors={propertyErrors}
                      placeholder="0"
                      min="0"
                      {...registerProperty('square_footage', {
                        ...validationRules.positiveNumber,
                        validate: (value) => {
                          if (!value) return true;
                          const num = parseInt(value);
                          if (isNaN(num) || num < 0 || num > 100000) {
                            return 'Square footage must be between 0 and 100,000';
                          }
                          return true;
                        }
                      })}
                    />
                    <ValidatedInput
                      name="year_built"
                      label="Year Built"
                      type="number"
                      register={registerProperty}
                      errors={propertyErrors}
                      placeholder="2024"
                      min="1800"
                      max={new Date().getFullYear()}
                      {...registerProperty('year_built', {
                        ...validationRules.year,
                        validate: (value) => {
                          if (!value) return true;
                          const year = parseInt(value);
                          const currentYear = new Date().getFullYear();
                          if (isNaN(year) || year < 1800 || year > currentYear) {
                            return `Year must be between 1800 and ${currentYear}`;
                          }
                          return true;
                        }
                      })}
                    />
                  </div>

                  <ValidatedTextarea
                    name="property_description"
                    label="Property Description"
                    register={registerProperty}
                    errors={propertyErrors}
                    placeholder="Describe the property features, layout, etc."
                    rows={3}
                    {...registerProperty('property_description', {
                      ...validationRules.maxLength(2000, 'Property description')
                    })}
                  />

                  <ValidatedTextarea
                    name="amenities"
                    label="Amenities"
                    register={registerProperty}
                    errors={propertyErrors}
                    placeholder="List amenities (pool, garage, fireplace, etc.)"
                    rows={2}
                    {...registerProperty('amenities', {
                      ...validationRules.maxLength(1000, 'Amenities')
                    })}
                  />

                  <ValidatedInput
                    name="address"
                    label="Address"
                    type="text"
                    register={registerProperty}
                    errors={propertyErrors}
                    placeholder="Enter street address"
                    icon={MapPin}
                    required
                    {...registerProperty('address', {
                      required: 'Address is required',
                      minLength: {
                        value: 5,
                        message: 'Address must be at least 5 characters'
                      }
                    })}
                  />

                  <div className="grid grid-cols-3 gap-4">
                    <ValidatedInput
                      name="city"
                      label="City"
                      type="text"
                      register={registerProperty}
                      errors={propertyErrors}
                      placeholder="Enter city"
                      required
                      {...registerProperty('city', {
                        required: 'City is required',
                        minLength: {
                          value: 2,
                          message: 'City must be at least 2 characters'
                        },
                        pattern: {
                          value: /^[a-zA-Z\s'-]+$/,
                          message: 'City can only contain letters, spaces, hyphens, and apostrophes'
                        }
                      })}
                    />
                    <ValidatedInput
                      name="state"
                      label="State"
                      type="text"
                      register={registerProperty}
                      errors={propertyErrors}
                      placeholder="CA"
                      required
                      maxLength={2}
                      {...registerProperty('state', validationRules.state)}
                    />
                    <ValidatedInput
                      name="zip_code"
                      label="ZIP Code"
                      type="text"
                      register={registerProperty}
                      errors={propertyErrors}
                      placeholder="12345"
                      required
                      {...registerProperty('zip_code', validationRules.zipCode)}
                    />
                  </div>

                  <ValidatedInput
                    name="property_value"
                    label="Current Property Value"
                    type="number"
                    register={registerProperty}
                    errors={propertyErrors}
                    placeholder="0"
                    icon={DollarSign}
                    {...registerProperty('property_value', validationRules.currency)}
                  />
                  <p className="text-xs text-gray-500 mt-1">Current market value (auto-updated from Zillow for USA properties)</p>

                  {/* Mortgage Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mortgage</label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="no"
                            {...registerProperty('has_mortgage')}
                            className="mr-2"
                          />
                          No
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="yes"
                            {...registerProperty('has_mortgage')}
                            className="mr-2"
                          />
                          Yes
                        </label>
                      </div>
                      
                      {watchedHasMortgage === 'yes' && (
                        <div className="grid grid-cols-3 gap-4 pl-4">
                          <ValidatedInput
                            name="loan_amount"
                            label="Loan Amount"
                            type="number"
                            register={registerProperty}
                            errors={propertyErrors}
                            placeholder="0"
                            icon={DollarSign}
                            {...registerProperty('loan_amount', validationRules.currency)}
                          />
                          <ValidatedInput
                            name="loan_rate"
                            label="Interest Rate (%)"
                            type="number"
                            register={registerProperty}
                            errors={propertyErrors}
                            placeholder="0"
                            step="0.01"
                            {...registerProperty('loan_rate', validationRules.percentage)}
                          />
                          <ValidatedInput
                            name="loan_term"
                            label="Loan Term (years)"
                            type="number"
                            register={registerProperty}
                            errors={propertyErrors}
                            placeholder="0"
                            {...registerProperty('loan_term', {
                              ...validationRules.positiveNumber,
                              validate: (value) => {
                                if (!value) return true;
                                const num = parseFloat(value);
                                if (isNaN(num) || num < 1 || num > 50) {
                                  return 'Loan term must be between 1 and 50 years';
                                }
                                return true;
                              }
                            })}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* USA Property Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">USA Property</label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="yes"
                            {...registerProperty('is_usa')}
                            className="mr-2"
                          />
                          Yes (USA)
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="no"
                            {...registerProperty('is_usa')}
                            className="mr-2"
                          />
                          No (International)
                        </label>
                      </div>
                      <p className="text-sm text-gray-500">
                        Only USA properties will have their values automatically updated from Zillow
                      </p>
                    </div>
                  </div>

                  <ValidatedInput
                    name="monthly_rent"
                    label="Monthly Rent"
                    type="number"
                    register={registerProperty}
                    errors={propertyErrors}
                    placeholder="0"
                    icon={DollarSign}
                    {...registerProperty('monthly_rent', validationRules.currency)}
                  />

                  <ValidatedInput
                    name="monthly_expenses"
                    label="Monthly Expenses"
                    type="number"
                    register={registerProperty}
                    errors={propertyErrors}
                    placeholder="0"
                    icon={DollarSign}
                    {...registerProperty('monthly_expenses', validationRules.currency)}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <ValidatedInput
                      name="purchase_date"
                      label="Purchase Date"
                      type="date"
                      register={registerProperty}
                      errors={propertyErrors}
                      icon={Calendar}
                      {...registerProperty('purchase_date', {
                        validate: (value) => {
                          if (!value) return true;
                          const date = new Date(value);
                          if (isNaN(date.getTime())) {
                            return 'Please enter a valid date';
                          }
                          const today = new Date();
                          if (date > today) {
                            return 'Purchase date cannot be in the future';
                          }
                          return true;
                        }
                      })}
                    />
                    <ValidatedInput
                      name="purchase_price"
                      label="Purchase Price (Cost Basis)"
                      type="number"
                      register={registerProperty}
                      errors={propertyErrors}
                      placeholder="0"
                      icon={DollarSign}
                      {...registerProperty('purchase_price', validationRules.currency)}
                    />
                  </div>
                  <p className="text-xs text-gray-500 -mt-3">Original purchase price used for depreciation calculation</p>

                  {watchedPropertyType === 'rental' && (
                    <ValidatedInput
                      name="rental_start_date"
                      label="Rental Start Date (for depreciation)"
                      type="date"
                      register={registerProperty}
                      errors={propertyErrors}
                      icon={Calendar}
                      {...registerProperty('rental_start_date', {
                        validate: (value) => {
                          if (!value) return true;
                          const date = new Date(value);
                          if (isNaN(date.getTime())) {
                            return 'Please enter a valid date';
                          }
                          return true;
                        }
                      })}
                    />
                  )}

                  <ValidatedTextarea
                    name="notes"
                    label="Notes"
                    register={registerProperty}
                    errors={propertyErrors}
                    placeholder="Additional notes about the property"
                    rows={3}
                    {...registerProperty('notes', {
                      ...validationRules.maxLength(1000, 'Notes')
                    })}
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddProperty(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Add Property
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Property Modal */}
      {showEditProperty && selectedProperty && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Property</h3>
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateProperty(); }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Property Name</label>
                    <input
                      type="text"
                      value={selectedProperty.name}
                      onChange={(e) => setSelectedProperty({...selectedProperty, name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Property Type</label>
                    <select
                      value={selectedProperty.type}
                      onChange={(e) => setSelectedProperty({...selectedProperty, type: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="rental">Rental</option>
                      <option value="primary">Primary Residence</option>
                      <option value="investment">Investment</option>
                      <option value="future">Future Development</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <input
                      type="text"
                      value={selectedProperty.address}
                      onChange={(e) => setSelectedProperty({...selectedProperty, address: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <input
                        type="text"
                        value={selectedProperty.city}
                        onChange={(e) => setSelectedProperty({...selectedProperty, city: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">State</label>
                      <input
                        type="text"
                        value={selectedProperty.state}
                        onChange={(e) => setSelectedProperty({...selectedProperty, state: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        maxLength="2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">ZIP Code</label>
                      <input
                        type="text"
                        value={selectedProperty.zip_code}
                        onChange={(e) => setSelectedProperty({...selectedProperty, zip_code: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Current Property Value</label>
                    <input
                      type="number"
                      value={selectedProperty.property_value}
                      onChange={(e) => setSelectedProperty({...selectedProperty, property_value: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Current market value (auto-updated from Zillow for USA properties)</p>
                  </div>

                  {/* Mortgage Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mortgage</label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="has_mortgage"
                            value="no"
                            checked={selectedProperty.has_mortgage === 'no'}
                            onChange={(e) => setSelectedProperty({...selectedProperty, has_mortgage: e.target.value})}
                            className="mr-2"
                          />
                          No
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="has_mortgage"
                            value="yes"
                            checked={selectedProperty.has_mortgage === 'yes'}
                            onChange={(e) => setSelectedProperty({...selectedProperty, has_mortgage: e.target.value})}
                            className="mr-2"
                          />
                          Yes
                        </label>
                      </div>
                      
                      {selectedProperty.has_mortgage === 'yes' && (
                        <div className="grid grid-cols-3 gap-4 pl-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Loan Amount</label>
                            <input
                              type="number"
                              value={selectedProperty.loan_amount}
                              onChange={(e) => setSelectedProperty({...selectedProperty, loan_amount: e.target.value})}
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Interest Rate (%)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={selectedProperty.loan_rate}
                              onChange={(e) => setSelectedProperty({...selectedProperty, loan_rate: e.target.value})}
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Loan Term (years)</label>
                            <input
                              type="number"
                              value={selectedProperty.loan_term}
                              onChange={(e) => setSelectedProperty({...selectedProperty, loan_term: e.target.value})}
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* USA Property Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">USA Property</label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="is_usa_edit"
                            value="yes"
                            checked={selectedProperty.is_usa === 'yes'}
                            onChange={(e) => setSelectedProperty({...selectedProperty, is_usa: e.target.value})}
                            className="mr-2"
                          />
                          Yes (USA)
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="is_usa_edit"
                            value="no"
                            checked={selectedProperty.is_usa === 'no'}
                            onChange={(e) => setSelectedProperty({...selectedProperty, is_usa: e.target.value})}
                            className="mr-2"
                          />
                          No (International)
                        </label>
                      </div>
                      <p className="text-sm text-gray-500">
                        Only USA properties will have their values automatically updated from Zillow
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Rent</label>
                    <input
                      type="number"
                      value={selectedProperty.monthly_rent}
                      onChange={(e) => setSelectedProperty({...selectedProperty, monthly_rent: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Purchase Date</label>
                      <input
                        type="date"
                        value={selectedProperty.purchase_date || ''}
                        onChange={(e) => setSelectedProperty({...selectedProperty, purchase_date: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Purchase Price (Cost Basis)</label>
                      <input
                        type="number"
                        value={selectedProperty.purchase_price || ''}
                        onChange={(e) => setSelectedProperty({...selectedProperty, purchase_price: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">Original purchase price used for depreciation calculation</p>
                    </div>
                  </div>

                  {selectedProperty.type === 'rental' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Rental Start Date <span className="text-xs text-gray-500">(for depreciation)</span>
                      </label>
                      <input
                        type="date"
                        value={selectedProperty.rental_start_date || ''}
                        onChange={(e) => setSelectedProperty({...selectedProperty, rental_start_date: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                      value={selectedProperty.notes}
                      onChange={(e) => setSelectedProperty({...selectedProperty, notes: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                    />
                  </div>

                  {/* Rental-specific fields - only show for rental properties */}
                  {selectedProperty.type === 'rental' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Rental Type</label>
                        <select
                          value={selectedProperty.rental_type || 'whole_house'}
                          onChange={(e) => setSelectedProperty({...selectedProperty, rental_type: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="whole_house">Whole House</option>
                          <option value="by_room">By Room</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Total Bedrooms</label>
                          <input
                            type="number"
                            value={selectedProperty.total_bedrooms || ''}
                            onChange={(e) => setSelectedProperty({...selectedProperty, total_bedrooms: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Total Bathrooms</label>
                          <input
                            type="number"
                            value={selectedProperty.total_bathrooms || ''}
                            onChange={(e) => setSelectedProperty({...selectedProperty, total_bathrooms: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Square Footage</label>
                          <input
                            type="number"
                            value={selectedProperty.square_footage || ''}
                            onChange={(e) => setSelectedProperty({...selectedProperty, square_footage: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Year Built</label>
                          <input
                            type="number"
                            value={selectedProperty.year_built || ''}
                            onChange={(e) => setSelectedProperty({...selectedProperty, year_built: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Property Description</label>
                        <textarea
                          value={selectedProperty.property_description || ''}
                          onChange={(e) => setSelectedProperty({...selectedProperty, property_description: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          rows="3"
                          placeholder="Describe the property features, layout, etc."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Amenities</label>
                        <textarea
                          value={selectedProperty.amenities || ''}
                          onChange={(e) => setSelectedProperty({...selectedProperty, amenities: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          rows="2"
                          placeholder="List amenities (pool, garage, etc.)"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowEditProperty(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Update Property
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Contract Modal */}
      {showAddContract && selectedProperty && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Renter Contract</h3>
              <p className="text-sm text-gray-600 mb-4">Property: {selectedProperty.name}</p>
              <form onSubmit={(e) => { e.preventDefault(); handleAddContract(); }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contract Type</label>
                    <select
                      value={newContract.contract_type}
                      onChange={(e) => setNewContract({...newContract, contract_type: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="whole_house">Whole House Contract</option>
                      <option value="individual_room">Individual Room Contract</option>
                    </select>
                  </div>

                  {/* Room Selection - Only show for individual room contracts */}
                  {newContract.contract_type === 'individual_room' && rooms.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Select Room</label>
                      <select
                        value={newContract.room_id || ''}
                        onChange={(e) => setNewContract({...newContract, room_id: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select a room...</option>
                        {rooms.map(room => (
                          <option key={room.id} value={room.id}>
                            {room.room_name} ({room.room_type}) - ${room.monthly_rent}/month
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tenant Name</label>
                    <input
                      type="text"
                      value={newContract.tenant_name}
                      onChange={(e) => setNewContract({...newContract, tenant_name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tenant Email</label>
                    <input
                      type="email"
                      value={newContract.tenant_email}
                      onChange={(e) => setNewContract({...newContract, tenant_email: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tenant Phone</label>
                    <input
                      type="tel"
                      value={newContract.tenant_phone}
                      onChange={(e) => setNewContract({...newContract, tenant_phone: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Rent</label>
                    <input
                      type="number"
                      value={newContract.monthly_rent}
                      onChange={(e) => setNewContract({...newContract, monthly_rent: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Security Deposit</label>
                    <input
                      type="number"
                      value={newContract.security_deposit}
                      onChange={(e) => setNewContract({...newContract, security_deposit: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Lease Start</label>
                      <input
                        type="date"
                        value={newContract.lease_start}
                        onChange={(e) => setNewContract({...newContract, lease_start: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Lease End</label>
                      <input
                        type="date"
                        value={newContract.lease_end}
                        onChange={(e) => setNewContract({...newContract, lease_end: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddContract(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Add Contract
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Room Modal */}
      {showAddRoom && selectedProperty && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Room</h3>
              <p className="text-sm text-gray-600 mb-4">Property: {selectedProperty.name}</p>
              <form onSubmit={handleAddRoom}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Room Name</label>
                    <input
                      type="text"
                      value={newRoom.room_name}
                      onChange={(e) => setNewRoom({...newRoom, room_name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Master Bedroom, Room 1"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Room Type</label>
                    <select
                      value={newRoom.room_type}
                      onChange={(e) => setNewRoom({...newRoom, room_type: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="bedroom">Bedroom</option>
                      <option value="bathroom">Bathroom</option>
                      <option value="kitchen">Kitchen</option>
                      <option value="living_room">Living Room</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Room Description</label>
                    <textarea
                      value={newRoom.room_description}
                      onChange={(e) => setNewRoom({...newRoom, room_description: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                      placeholder="Describe the room features, size, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Rent</label>
                    <input
                      type="number"
                      value={newRoom.monthly_rent}
                      onChange={(e) => setNewRoom({...newRoom, monthly_rent: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddRoom(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Add Room
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Tenant Modal */}
      {showAddTenant && selectedProperty && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Tenant</h3>
              <p className="text-sm text-gray-600 mb-4">Property: {selectedProperty.name}</p>
              <form onSubmit={handleAddTenant}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <input
                      type="text"
                      value={newTenant.first_name}
                      onChange={(e) => setNewTenant({...newTenant, first_name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                      type="text"
                      value={newTenant.last_name}
                      onChange={(e) => setNewTenant({...newTenant, last_name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={newTenant.email}
                      onChange={(e) => setNewTenant({...newTenant, email: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={newTenant.phone}
                      onChange={(e) => setNewTenant({...newTenant, phone: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Driver's License</label>
                    <input
                      type="text"
                      value={newTenant.drivers_license}
                      onChange={(e) => setNewTenant({...newTenant, drivers_license: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">License State</label>
                    <input
                      type="text"
                      value={newTenant.drivers_license_state}
                      onChange={(e) => setNewTenant({...newTenant, drivers_license_state: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      maxLength="2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">License Expiry</label>
                    <input
                      type="date"
                      value={newTenant.drivers_license_expiry}
                      onChange={(e) => setNewTenant({...newTenant, drivers_license_expiry: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Income</label>
                    <input
                      type="number"
                      value={newTenant.monthly_income}
                      onChange={(e) => setNewTenant({...newTenant, monthly_income: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Credit Score</label>
                    <input
                      type="number"
                      value={newTenant.credit_score}
                      onChange={(e) => setNewTenant({...newTenant, credit_score: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      min="300"
                      max="850"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Employment Company</label>
                    <input
                      type="text"
                      value={newTenant.employment_company}
                      onChange={(e) => setNewTenant({...newTenant, employment_company: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Employment Position</label>
                    <input
                      type="text"
                      value={newTenant.employment_position}
                      onChange={(e) => setNewTenant({...newTenant, employment_position: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Emergency Contact Name</label>
                    <input
                      type="text"
                      value={newTenant.emergency_contact_name}
                      onChange={(e) => setNewTenant({...newTenant, emergency_contact_name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Emergency Contact Phone</label>
                    <input
                      type="tel"
                      value={newTenant.emergency_contact_phone}
                      onChange={(e) => setNewTenant({...newTenant, emergency_contact_phone: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                      value={newTenant.notes}
                      onChange={(e) => setNewTenant({...newTenant, notes: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddTenant(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Add Tenant
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyManager;
